// BB.2 — Server functions do Business Plan (bundle + save).
//
// Nenhuma migration. Fonte canônica continua sendo `business_workspace`.
// Estas funções são camada de leitura/escrita coesa que compõe:
//   1) plan derivado (adapter)
//   2) mapa bloco → missão (getBlockMissionMap-like)
//   3) usabilidade de cada missão (mission-guard)
//   4) resolução de editor por seção (editor-resolver)
//   5) próximo passo canônico (resolver)
//
// Escrita: `saveStructuredSection` faz merge parcial no bloco, respeitando
// dados legados e chaves internas (_mission_id, _finalized_at).

import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { buildBusinessPlan, type BusinessPlan } from "./adapter";
import { resolveNextBusinessPlanStep, type NextBusinessPlanStep } from "./resolver";
import { evaluateMissionUsability, type MissionUsability } from "./mission-guard";
import {
  resolveAllSectionEditors,
  resolveBusinessPlanEditor,
  type SectionEditorResolution,
} from "./editor-resolver";
import {
  ALL_STRUCTURED_CONFIGS,
  getStructuredConfig,
  serializeFieldValue,
  type StructuredField,
} from "./structured-fields";
import { BUSINESS_PLAN_SECTIONS, IMPACTED_BY_SECTION, SECTION_BY_KEY, editorialLabel, type BlockKey } from "./schema";
import { buildFullBusinessPlan, type FullBusinessPlan } from "./full-plan";
import { getMethodologyById } from "@/lib/methodology/registry";

const ALLOWED_BLOCKS = new Set([
  "model", "audience", "niche", "offer", "products",
  "pricing", "brand", "channels", "sales_plan",
  "prospects", "proposals", "extras",
]);

async function getActiveProject(supabase: any, userId: string) {
  const { data } = await supabase
    .from("user_projects")
    .select("id, business_id, template_id, template_version_id, status")
    .eq("user_id", userId)
    .eq("status", "active")
    .maybeSingle();
  return data ?? null;
}

async function ensureWorkspaceRow(supabase: any, userId: string, projectId: string) {
  const { data: existing } = await supabase
    .from("business_workspace")
    .select("*")
    .eq("project_id", projectId)
    .maybeSingle();
  if (existing) return existing;
  const { data: created } = await supabase
    .from("business_workspace")
    .insert({ user_id: userId, project_id: projectId })
    .select("*")
    .single();
  return created;
}

// Descobre missões da jornada e resume usabilidade.
async function loadMissionMetadata(supabase: any, project: any): Promise<{
  missionMap: Partial<Record<BlockKey, string | null>>;
  usability: Record<string, MissionUsability>;
}> {
  let missionIds: string[] = [];

  if (project?.template_version_id) {
    const { data: version } = await supabase
      .from("journey_versions")
      .select("snapshot")
      .eq("id", project.template_version_id)
      .maybeSingle();
    const stages = ((version?.snapshot as any)?.stages ?? []) as any[];
    const snapshotUsability: Record<string, MissionUsability> = {};
    const snapshotMap: Partial<Record<BlockKey, string | null>> = {};
    for (const s of stages) {
      for (const m of s.missions ?? []) {
        missionIds.push(m.id);
        const target = m.deliverable?.target_field ?? null;
        const usab = evaluateMissionUsability({
          missionId: m.id,
          hasSlides: (m.slides?.length ?? 0) > 0,
          hasQuestions: (m.questions?.length ?? 0) > 0,
          hasDeliverable: !!m.deliverable,
          targetField: target,
        });
        snapshotUsability[m.id] = usab;
        if (target && ALLOWED_BLOCKS.has(target) && !(target in snapshotMap)) {
          snapshotMap[target as BlockKey] = m.id;
        }
      }
    }
    if (missionIds.length > 0) return { missionMap: snapshotMap, usability: snapshotUsability };
  }

  if (missionIds.length === 0 && project?.template_id) {
    const { data: stages } = await supabase
      .from("journey_stages")
      .select("id, journey_missions(id)")
      .eq("template_id", project.template_id)
      .is("deleted_at", null);
    for (const s of (stages ?? [])) {
      for (const m of ((s as any).journey_missions ?? [])) missionIds.push(m.id);
    }
  }

  if (missionIds.length === 0) return { missionMap: {}, usability: {} };

  const [{ data: sl }, { data: qs }, { data: dl }] = await Promise.all([
    supabase.from("mission_slides").select("mission_id").in("mission_id", missionIds).is("deleted_at", null),
    supabase.from("mission_questions").select("mission_id").in("mission_id", missionIds).is("deleted_at", null),
    supabase.from("mission_deliverables").select("mission_id, target_field").in("mission_id", missionIds).is("deleted_at", null),
  ]);
  const slC = new Set<string>();
  const qC = new Set<string>();
  const dMap = new Map<string, string | null>();
  for (const r of sl ?? []) slC.add((r as any).mission_id);
  for (const r of qs ?? []) qC.add((r as any).mission_id);
  for (const r of dl ?? []) dMap.set((r as any).mission_id, (r as any).target_field ?? null);

  const usability: Record<string, MissionUsability> = {};
  const missionMap: Partial<Record<BlockKey, string | null>> = {};
  for (const id of missionIds) {
    const target = dMap.get(id) ?? null;
    usability[id] = evaluateMissionUsability({
      missionId: id,
      hasSlides: slC.has(id),
      hasQuestions: qC.has(id),
      hasDeliverable: dMap.has(id),
      targetField: target,
    });
    if (target && ALLOWED_BLOCKS.has(target) && !(target in missionMap)) {
      missionMap[target as BlockKey] = id;
    }
  }
  return { missionMap, usability };
}

export type BusinessPlanBundle = {
  hasProject: boolean;
  project: any | null;
  business: any | null;
  plan: BusinessPlan | null;
  editorsBySection: Record<BlockKey, SectionEditorResolution> | null;
  nextStep: NextBusinessPlanStep | null;
};

export const getBusinessPlanBundle = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<BusinessPlanBundle> => {
    const { supabase, userId } = context;
    const project = await getActiveProject(supabase, userId);
    if (!project) {
      return { hasProject: false, project: null, business: null, plan: null, editorsBySection: null, nextStep: null };
    }
    const [workspaceRow, { data: business }, missionMeta] = await Promise.all([
      ensureWorkspaceRow(supabase, userId, project.id),
      supabase
        .from("businesses")
        .select("id, slug, title, summary, category:categories(name)")
        .eq("id", project.business_id)
        .maybeSingle(),
      loadMissionMetadata(supabase, project),
    ]);

    const plan = buildBusinessPlan({
      projectId: project.id,
      templateVersionId: project.template_version_id ?? null,
      workspace: workspaceRow ?? null,
      missionMap: missionMeta.missionMap,
    });

    const editorsBySection = resolveAllSectionEditors({
      plan,
      missionUsability: missionMeta.usability,
    });

    const nextStep = resolveNextBusinessPlanStep({ plan });

    return {
      hasProject: true,
      project,
      business,
      plan,
      editorsBySection,
      nextStep,
    };
  });

// ---------- BB.3: bundle com plano completo derivado ----------

export type FullBusinessPlanBundle = {
  hasProject: boolean;
  bundle: BusinessPlanBundle;
  fullPlan: FullBusinessPlan | null;
};

export const getFullBusinessPlanBundle = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<FullBusinessPlanBundle> => {
    const { supabase, userId } = context;
    const project = await getActiveProject(supabase, userId);
    if (!project) {
      return {
        hasProject: false,
        bundle: { hasProject: false, project: null, business: null, plan: null, editorsBySection: null, nextStep: null },
        fullPlan: null,
      };
    }
    const [workspaceRow, { data: business }, missionMeta] = await Promise.all([
      ensureWorkspaceRow(supabase, userId, project.id),
      supabase
        .from("businesses")
        .select("id, slug, title, summary, category:categories(name)")
        .eq("id", project.business_id)
        .maybeSingle(),
      loadMissionMetadata(supabase, project),
    ]);
    const plan = buildBusinessPlan({
      projectId: project.id,
      templateVersionId: project.template_version_id ?? null,
      workspace: workspaceRow ?? null,
      missionMap: missionMeta.missionMap,
    });
    const editorsBySection = resolveAllSectionEditors({
      plan, missionUsability: missionMeta.usability,
    });
    const nextStep = resolveNextBusinessPlanStep({ plan });
    const methodology = getMethodologyById(project.template_id ?? null);
    const fullPlan = buildFullBusinessPlan({
      plan,
      editorsBySection,
      workspace: workspaceRow ?? null,
      business: business ? { title: business.title, summary: business.summary ?? null } : null,
      methodology,
      missionUsability: missionMeta.usability,
    });
    return {
      hasProject: true,
      bundle: { hasProject: true, project, business, plan, editorsBySection, nextStep },
      fullPlan,
    };
  });


export type SaveStructuredSectionInput = {
  sectionKey: BlockKey;
  values: Record<string, unknown>;
  /** Se true, marca `_completed_at`; caso contrário, salva apenas rascunho. */
  complete: boolean;
};

export type SectionStatusChange = {
  before: "empty" | "in_progress" | "complete" | "attention" | "blocked";
  after: "empty" | "in_progress" | "complete" | "attention" | "blocked";
};

export type SaveStructuredSectionResult = {
  ok: true;
  plan: BusinessPlan;
  editorsBySection: Record<BlockKey, SectionEditorResolution>;
  savedSection: BlockKey;
  /** Campos cujo valor mudou nessa gravação (label editorial + valor novo). */
  changedFields: Array<{ key: string; label: string; value: string }>;
  /** Seções bloqueadas antes que agora estão liberadas. */
  unlocked: BlockKey[];
  /** Seções que declaram esta como dependência — podem ser revisadas agora. */
  impactedSections: BlockKey[];
  /** Transição de status da seção salva. */
  statusChange: SectionStatusChange;
  nextStep: NextBusinessPlanStep | null;
};

export const saveStructuredSection = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: SaveStructuredSectionInput) => {
    if (!d?.sectionKey) throw new Error("sectionKey obrigatório");
    if (!ALLOWED_BLOCKS.has(d.sectionKey)) throw new Error("Seção inválida.");
    if (!d.values || typeof d.values !== "object") throw new Error("values obrigatório");
    return d;
  })
  .handler(async ({ data, context }): Promise<SaveStructuredSectionResult> => {
    const { supabase, userId } = context;
    const project = await getActiveProject(supabase, userId);
    if (!project) throw new Error("Nenhum projeto ativo.");

    const config = getStructuredConfig(data.sectionKey);
    if (!config) throw new Error("Editor estruturado indisponível para esta seção.");

    // Plano antes → para calcular seções desbloqueadas.
    const workspaceBefore = await ensureWorkspaceRow(supabase, userId, project.id);
    const missionMetaBefore = await loadMissionMetadata(supabase, project);
    const planBefore = buildBusinessPlan({
      projectId: project.id,
      templateVersionId: project.template_version_id ?? null,
      workspace: workspaceBefore ?? null,
      missionMap: missionMetaBefore.missionMap,
    });
    const blockedBefore = new Set(planBefore.sections.filter((s) => s.status === "blocked").map((s) => s.section.key));

    // Serializar valores conforme spec de campos.
    const fieldByKey: Record<string, StructuredField> = Object.fromEntries(
      config.fields.map((f) => [f.key, f]),
    );

    const currentBlock = (workspaceBefore as any)[config.block] ?? {};
    const nextBlock: Record<string, unknown> = { ...currentBlock };
    const changedFields: Array<{ key: string; label: string; value: string }> = [];

    const displayValue = (v: unknown): string => {
      if (v == null) return "";
      if (Array.isArray(v)) return v.map((x) => (typeof x === "string" ? x : String((x as any)?.label ?? (x as any)?.value ?? ""))).filter(Boolean).join(", ");
      if (typeof v === "object") return String((v as any).label ?? (v as any).value ?? "");
      return String(v);
    };

    for (const [key, raw] of Object.entries(data.values)) {
      const field = fieldByKey[key];
      if (!field) continue; // ignora chaves não declaradas — não apagamos legado
      const serialized = serializeFieldValue(field, raw);
      const previous = currentBlock[key];
      const isCleared = serialized === "" || (Array.isArray(serialized) && serialized.length === 0);
      if (isCleared) {
        if (previous != null && previous !== "") {
          delete nextBlock[key];
          changedFields.push({ key, label: editorialLabel(key), value: "(removido)" });
        }
        continue;
      }
      const prevDisp = displayValue(previous);
      const nextDisp = displayValue(serialized);
      if (prevDisp !== nextDisp) {
        nextBlock[key] = serialized;
        changedFields.push({ key, label: editorialLabel(key), value: nextDisp });
      } else {
        nextBlock[key] = serialized;
      }
    }

    if (data.complete) {
      nextBlock._completed_at = new Date().toISOString();
      nextBlock._completion_source = "structured_editor";
    } else {
      nextBlock._draft_updated_at = new Date().toISOString();
    }

    const { data: updated, error } = await (supabase as any)
      .from("business_workspace")
      .update({ [config.block]: nextBlock })
      .eq("id", workspaceBefore.id)
      .select("*")
      .single();
    if (error) throw error;

    // Recalcula plano após save.
    const missionMetaAfter = missionMetaBefore; // metadata de missões não muda
    const planAfter = buildBusinessPlan({
      projectId: project.id,
      templateVersionId: project.template_version_id ?? null,
      workspace: updated ?? null,
      missionMap: missionMetaAfter.missionMap,
    });
    const editorsBySection = resolveAllSectionEditors({
      plan: planAfter,
      missionUsability: missionMetaAfter.usability,
    });
    const nextStep = resolveNextBusinessPlanStep({ plan: planAfter });

    const unlocked: BlockKey[] = planAfter.sections
      .filter((s) => blockedBefore.has(s.section.key) && s.status !== "blocked")
      .map((s) => s.section.key);

    const before = planBefore.sections.find((s) => s.section.key === data.sectionKey);
    const after = planAfter.sections.find((s) => s.section.key === data.sectionKey);
    const statusChange = {
      before: (before?.status ?? "empty") as any,
      after: (after?.status ?? "empty") as any,
    };

    // Seções impactadas: as que declaram esta como dependência e já têm algum conteúdo.
    const impactedSections: BlockKey[] = (IMPACTED_BY_SECTION[data.sectionKey] ?? [])
      .filter((k) => {
        const s = planAfter.sections.find((x) => x.section.key === k);
        return s && s.status !== "empty";
      });

    // Analytics: reaproveita event_name existente ("mission_completed"/"mission_resumed").
    try {
      await supabase.from("analytics_events").insert({
        user_id: userId,
        event_name: data.complete ? "mission_completed" : "mission_resumed",
        metadata: {
          via: "business_plan_structured_editor",
          section_key: data.sectionKey,
          status_after: after?.status ?? null,
          unlocked,
          impacted: impactedSections,
          changed_fields: changedFields.map((f) => f.key),
        } as never,
      });
    } catch { /* non-fatal */ }

    return {
      ok: true,
      plan: planAfter,
      editorsBySection,
      savedSection: data.sectionKey,
      changedFields,
      unlocked,
      impactedSections,
      statusChange,
      nextStep,
    };
  });

// ---------- read: resolução para uma seção específica ----------
// (usado pelo route dispatcher para decidir para onde mandar o usuário).
export const getSectionEditor = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { sectionKey: BlockKey }) => {
    if (!d?.sectionKey) throw new Error("sectionKey obrigatório");
    return d;
  })
  .handler(async ({ data, context }): Promise<{
    resolution: SectionEditorResolution | null;
    plan: BusinessPlan | null;
    workspaceBlock: Record<string, any> | null;
    businessTitle: string | null;
  }> => {
    const { supabase, userId } = context;
    const project = await getActiveProject(supabase, userId);
    if (!project) return { resolution: null, plan: null, workspaceBlock: null, businessTitle: null };
    const [workspaceRow, missionMeta, businessRow] = await Promise.all([
      ensureWorkspaceRow(supabase, userId, project.id),
      loadMissionMetadata(supabase, project),
      project.business_id
        ? supabase.from("businesses").select("title").eq("id", project.business_id).maybeSingle()
        : Promise.resolve({ data: null }),
    ]);
    const plan = buildBusinessPlan({
      projectId: project.id,
      templateVersionId: project.template_version_id ?? null,
      workspace: workspaceRow ?? null,
      missionMap: missionMeta.missionMap,
    });
    const resolution = resolveBusinessPlanEditor(data.sectionKey, {
      plan,
      missionUsability: missionMeta.usability,
    });
    const raw = (workspaceRow as any)?.[data.sectionKey];
    const block: Record<string, any> | null =
      raw && typeof raw === "object" && !Array.isArray(raw) ? (raw as Record<string, any>) : null;
    const businessTitle = ((businessRow as any)?.data?.title as string | undefined) ?? null;
    return { resolution, plan, workspaceBlock: block, businessTitle };
  });

// export helpers for structured client use
export { ALL_STRUCTURED_CONFIGS, BUSINESS_PLAN_SECTIONS };
